import type { LensCard, LensCardData, LensDataset } from '~/types/leLens';

import { cardComparison } from './cardComparison';
import type { Shaped } from './cardView';
import { applyView } from './cardView';
import { buildComparisonTable, namedTableRatioFormats } from './cards/tableComparison';
import { applyRowFields, prepareFields } from './datasetFields';
import type { RowsDrawerChoices } from './lensRowsDrawerStorage';

export const AUTO_OPEN_TABLE_MAX_ROWS = 8;
export const AUTO_OPEN_TABLE_MAX_COLUMNS = 6;
export const AUTO_OPEN_TABLE_MAX_COUNT = 2;

/** A complete, compact answer belongs on the board. Detail and unknown
 * results wait behind Rows until the reader chooses to open them. */
export function shouldAutoOpenTable(card: LensCard, data?: LensCardData, dataset?: LensDataset): boolean {
  if (
    card.type !== 'table' ||
    data?.status !== 'ready' ||
    data.truncated ||
    !data.rows?.length ||
    !data.columns?.length
  ) {
    return false;
  }
  const shape = (result: Pick<LensCardData, 'columns' | 'rows' | 'aggregated'>): Shaped => {
    const columns = result.columns ?? [];
    const prepared = prepareFields(dataset?.fields, columns);
    return applyView(
      applyRowFields(result.rows ?? [], prepared.row),
      [...columns, ...prepared.row.map((entry) => entry.field.name)],
      card.view,
      'table',
      prepared.aggregate,
      result.aggregated,
      dataset?.columns,
    );
  };
  const current = shape(data);
  const companion = cardComparison(data)?.data;
  if (companion && (companion.truncated || !['ready', 'empty'].includes(companion.status))) return false;
  // Comparison columns and unmatched prior groups are visible too. A small
  // base query can draw a wide, dense table once its comparison is included.
  const displayed =
    companion || card.config?.tableColumns
      ? buildComparisonTable({
          card,
          current,
          comparison: companion?.status === 'ready' ? shape(companion) : undefined,
          hasComparison: Boolean(companion),
          currentComplete: true,
          ratioFormats: namedTableRatioFormats(card, dataset?.columns),
        }).shaped
      : current;
  return (
    displayed.rows.length > 0 &&
    displayed.rows.length <= AUTO_OPEN_TABLE_MAX_ROWS &&
    displayed.columns.length <= AUTO_OPEN_TABLE_MAX_COLUMNS
  );
}

export function expandedRowsForResults(
  cards: ReadonlyArray<LensCard>,
  dataFor: (id: string) => LensCardData | undefined,
  choices: RowsDrawerChoices,
  datasets: ReadonlyArray<LensDataset> = [],
): ReadonlySet<string> {
  const tables = cards.filter((card) => card.type === 'table');
  const openAllTables = tables.length <= AUTO_OPEN_TABLE_MAX_COUNT;
  return new Set(
    tables
      .filter((card) => {
        return Object.prototype.hasOwnProperty.call(choices, card.id)
          ? choices[card.id]
          : openAllTables ||
              shouldAutoOpenTable(
                card,
                dataFor(card.id),
                datasets.find((dataset) => dataset.id === card.datasetId),
              );
      })
      .map((card) => card.id),
  );
}
